import { Fragment } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { SxProps, Theme } from '@mui/material/styles';
import CancelConfirmModal from './CancelConfirmModal';

import { useState } from 'react';

import IRunEntry from 'types/IRunEntry';
import RunState from 'types/RunState';

export type CancellableRun = Pick<IRunEntry, 'id' | 'name' | 'state'> & {
  task_state_count?: Record<string, number>;
};

const CANCELLABLE_TASK_STATES: RunState[] = ['WAITING', 'READY', 'RESERVED', 'RUNNING', 'EVICTING', 'PAUSING'];
const IN_FLIGHT_STATES: RunState[] = ['RUNNING', 'PAUSING', 'EVICTING'];
const CANCELLABLE_AGGREGATE_STATES: RunState[] = ['RUNNING', 'READY', 'WAITING', 'RESERVED', 'FAILED'];

function hasAny(counts: Record<string, number>, states: RunState[]): boolean {
  return states.some((state) => (counts[state] ?? 0) > 0);
}

function usableCounts(counts?: Record<string, number>): Record<string, number> | undefined {
  if (!counts) return undefined;
  return Object.values(counts).some((n) => n > 0) ? counts : undefined;
}

export function countTaskStates(states: RunState[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const state of states) counts[state] = (counts[state] ?? 0) + 1;
  return counts;
}

export function isCancellableRun(run: CancellableRun): boolean {
  const counts = usableCounts(run.task_state_count);
  return counts ? hasAny(counts, CANCELLABLE_TASK_STATES) : CANCELLABLE_AGGREGATE_STATES.includes(run.state);
}

export function hasInFlightWork(run: CancellableRun): boolean {
  const counts = usableCounts(run.task_state_count);
  return counts ? hasAny(counts, IN_FLIGHT_STATES) : run.state === 'RUNNING';
}

function cancelTooltip(selected: CancellableRun[], cancellable: number): string {
  if (selected.length === 0) return 'Select workflows to cancel';
  if (cancellable > 0) {
    return selected.length === 1 ? 'Cancel this workflow' : `Cancel selected workflows (${cancellable})`;
  }
  const only = selected.length === 1 ? selected[0] : undefined;
  const counts = only && usableCounts(only.task_state_count);
  if (counts && hasAny(counts, ['PAUSED']) && !hasAny(counts, CANCELLABLE_TASK_STATES)) {
    return 'Only checkpointed tasks remain — resume first to cancel';
  }
  return only ? 'This workflow cannot be cancelled' : 'No selected workflows can be cancelled';
}

export default function CancelButton({
  selectedRuns,
  onSuccess,
  variant = 'contained',
  size = 'medium',
  sx,
}: {
  selectedRuns: CancellableRun[];
  onSuccess?: () => void;
  variant?: 'contained' | 'outlined';
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}) {
  const cancellableRuns = selectedRuns.filter(isCancellableRun);

  const [showModal, setShowModal] = useState(false);

  const tooltip = cancelTooltip(selectedRuns, cancellableRuns.length);

  return (
    <Fragment>
      <Tooltip title={tooltip} placement="bottom-start">
        <span>
          <Button
            variant={variant}
            color="primary"
            size={size}
            sx={sx}
            disabled={cancellableRuns.length === 0}
            onClick={() => setShowModal(true)}
          >
            Cancel
          </Button>
        </span>
      </Tooltip>

      {showModal && (
        <CancelConfirmModal onClose={() => setShowModal(false)} selectedRuns={cancellableRuns} onSuccess={onSuccess} />
      )}
    </Fragment>
  );
}
